import React, { useEffect, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import './MusicPreview.css';

const API_BASE    = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const LIVE_URL    = `${API_BASE}/api/music/latest`;
const MUSIC_URL   = process.env.PUBLIC_URL + '/assets/music.json';
// Live cron-refreshed videos first, static build asset as fallback.
const VIDEOS_API    = `${API_BASE}/api/videos`;
const VIDEOS_STATIC = process.env.PUBLIC_URL + '/assets/videos.json';
const SPOTIFY_URL = 'https://open.spotify.com/artist/1QhaqxeqF9sipS2gwbEKpu';
const FALLBACK_COVER =
  'https://lastfm.freetls.fastly.net/i/u/ar0/eb73f7858c3331fb2da6c25615d67409.jpg?version=1';

/** "C-Kan, X - Title (Official Visualizer)" → "Title" */
function cleanVideoTitle(raw) {
  let s = String(raw || '');
  s = s.replace(/^c-kan(?:,\s*[^-]+?)?\s*-\s*/i, '');
  s = s.replace(/\s*\([^)]*\)\s*$/, '');
  return s.trim();
}

function ytThumb(id, q = 'maxresdefault') {
  return `https://img.youtube.com/vi/${id}/${q}.jpg`;
}

function typeLabel(type, t, lang) {
  const map = {
    es: { single: 'Nuevo sencillo', EP: 'Nuevo EP', album: 'Nuevo álbum' },
    en: { single: 'New single',     EP: 'New EP',   album: 'New album' },
  };
  return (map[lang] || map.es)[type] || t('music.featuredTag');
}

export default function MusicPreview() {
  const { t, lang } = useT();
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    let mounted = true;

    /* Resolution chain:
       1. Live  — /api/music/latest (Deezer-backed, real-time)
       2. Static — /assets/music.json (manual fallback)
       3. Video  — /assets/videos.json (latest YouTube drop)
       4. i18n   — translations literal fallback
    */
    (async () => {
      // 1. Live music feed
      try {
        const r = await fetch(LIVE_URL, { cache: 'no-store' });
        if (r.ok) {
          const data = await r.json();
          const m = data?.latest;
          if (m?.id && m?.title) {
            if (!mounted) return;
            setFeatured({
              tag:   typeLabel(m.type, t, lang),
              title: m.title,
              cover: m.cover || FALLBACK_COVER,
              href:  m.deezer || SPOTIFY_URL,
              kind:  'music',
            });
            return;
          }
        }
      } catch { /* live source unreachable — fall through */ }

      // 2. Static music.json
      try {
        const r = await fetch(MUSIC_URL, { cache: 'no-store' });
        if (r.ok) {
          const data = await r.json();
          const first = (data?.music || [])[0];
          if (first?.id) {
            if (!mounted) return;
            setFeatured({
              tag:   typeLabel(first.type, t, lang),
              title: first.title,
              cover: first.cover || FALLBACK_COVER,
              href:  first.spotify || first.apple || first.youtube || SPOTIFY_URL,
              kind:  'music',
            });
            return;
          }
        }
      } catch { /* fall through */ }

      // 3. Video fallback (live API first, static asset second)
      for (const url of [VIDEOS_API, VIDEOS_STATIC]) {
        try {
          const r = await fetch(url, { cache: 'no-store' });
          if (!r.ok) continue;
          const data = await r.json();
          const v = (data?.videos || [])[0];
          if (v?.id && mounted) {
            setFeatured({
              tag:   t('music.featuredTag'),
              title: cleanVideoTitle(v.title) || v.title,
              cover: ytThumb(v.id, 'maxresdefault'),
              href:  `https://youtu.be/${v.id}`,
              kind:  'video',
            });
          }
          break; // got a usable response — don't try the fallback source
        } catch { /* try the next source; i18n defaults cover total failure */ }
      }
    })();

    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const featuredTag   = featured?.tag   || t('music.featuredTag');
  const featuredTitle = featured?.title || t('music.featuredTitle');
  const featuredCover = featured?.cover || FALLBACK_COVER;
  const featuredHref  = featured?.href  || SPOTIFY_URL;

  const STREAMS = [
    {
      label: 'Spotify',
      tag:   t('music.spotifyTag'),
      value: '4.2M',
      href:  SPOTIFY_URL,
      color: '#1DB954',
      icon:  'fa-brands fa-spotify',
    },
    {
      label: 'Apple Music',
      tag:   t('music.appleTag'),
      value: t('music.appleValue'),
      href:  'https://music.apple.com/us/artist/c-kan/395579929',
      color: '#fa2d48',
      icon:  'fa-brands fa-apple',
    },
    {
      label: 'YouTube',
      tag:   t('music.youtubeTag'),
      value: '5.1M',
      href:  'https://www.youtube.com/channel/UCkD89TEFWsTZluY6tk84yqg',
      color: '#ff0033',
      icon:  'fa-brands fa-youtube',
    },
  ];

  return (
    <section className="music">
      <div className="music__bg" aria-hidden="true" />
      <div className="music__inner">
        <header className="music__head">
          <span className="ck-eyebrow">{t('music.eyebrow')}</span>
          <h2 className="ck-section-title">
            {t('music.titleA')} <em>{t('music.titleB')}</em>
          </h2>
        </header>

        <div className="music__grid">
          {/* ── Featured release ── */}
          <article className="music__featured">
            <div className="music__cover">
              <img
                key={featuredCover}
                src={featuredCover}
                alt={featuredTitle}
                loading="lazy"
                onError={(e) => {
                  const src = e.currentTarget.src;
                  if (src.includes('img.youtube.com') && src.includes('maxresdefault')) {
                    e.currentTarget.src = src.replace('maxresdefault', 'hqdefault');
                  } else if (src !== FALLBACK_COVER) {
                    e.currentTarget.src = FALLBACK_COVER;
                  }
                }}
              />
              <div className="music__cover-glow" />
              <a
                href={featuredHref}
                target="_blank"
                rel="noopener noreferrer"
                className="music__play"
                aria-label={t('music.playLabel')}
              >
                <i className="fa-solid fa-play" />
              </a>
              <div className="music__cover-corner">
                <span>{t('music.latestRelease')}</span>
              </div>
            </div>

            <div className="music__featured-meta">
              <span className="music__featured-tag">{featuredTag}</span>
              <h3 className="music__featured-title">{featuredTitle}</h3>
              <p className="music__featured-desc">{t('music.featuredDesc')}</p>
            </div>

            <div className="music__streams">
              {STREAMS.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="music__stream"
                  style={{ '--stream-color': s.color }}
                >
                  <i className={`${s.icon} music__stream-icon`} />
                  <div className="music__stream-text">
                    <span className="music__stream-label">{s.label}</span>
                    <span className="music__stream-tag">{s.tag}</span>
                  </div>
                  <span className="music__stream-value">{s.value}</span>
                </a>
              ))}
            </div>
          </article>

          {/* ── Spotify live player ── */}
          <aside className="music__player">
            <div className="music__player-head">
              <span className="music__player-dot" />
              {t('music.nowPlaying')}
            </div>
            <div className="music__player-embed">
              <iframe
                src="https://open.spotify.com/embed/artist/1QhaqxeqF9sipS2gwbEKpu?utm_source=generator&theme=0"
                title="C-Kan on Spotify"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
